/**
 * Disposition / instinct mapping — temperament primaries → AI knobs.
 * Single evaluation path: mapping.curve = "linear" via centered t∈[−1,1].
 *
 * External authored/UI/Sim absolute stay 0..100.
 * Internal: t = (axis − 50) / 50; value = base + slope·t (clamp min/max).
 *
 * Bond authored (JSON): stickyGain, adjutantFollowMul, healSplitMul.
 * Bond derived in code: minSwitch / leash / commitSec from sticky;
 * adjutantPriorityBonus from followMul.
 */
import {
  raidCatalog,
  raidAiAbsoluteFor,
  raidAiTuningFor,
  resolveInstinct,
  type RaidAxisId,
  type RaidDisposition,
} from "../../data/raid-catalog";
import type { RaidClassId } from "../../data/raid-catalog";
import { symmetrizeInsightAxes } from "./judgment/legacy-map";
import type { RaidUnit } from "./raid-types";

export type MappingCurve = "linear";

export type MappingEntry = {
  axis?: string;
  source?: string;
  base: number;
  /** Change in value as t goes −1→+1 (preferred). */
  slope?: number;
  /** @deprecated Legacy per-axis-point; absorbed as slope = per×50. */
  per?: number;
  min?: number;
  max?: number;
  curve?: MappingCurve;
  note?: string;
};

/** Matches adjutantFollowMul mapping max / ADJUTANT_FOLLOW_MUL_MAX. */
const FOLLOW_MUL_MAX_DEFAULT = 1.6;
/** Soft collective focus score at full follow. */
const PRIORITY_BONUS_AT_FULL_FOLLOW = 20;

/** Derived sticky family endpoints (personal01=1 → personal / bond0). */
const DERIVED_MIN_SWITCH = { collective: 2, personal: 16 } as const;
const DERIVED_LEASH_PX = { collective: 8, personal: 160 } as const;
const DERIVED_COMMIT_SEC = { collective: 0.02, personal: 0.55 } as const;

/**
 * Centered axis / instinct norm: 0→−1, 50→0, 100→+1.
 */
export function axisNorm(axis: number): number {
  return Math.max(-1, Math.min(1, (axis - 50) / 50));
}

function mappingSlope(m: MappingEntry): number {
  if (typeof m.slope === "number") return m.slope;
  return (m.per ?? 0) * 50;
}

/**
 * Linear centered: base + slope·t, t = axisNorm(axis), clamped to min/max.
 */
export function clampMapped(m: MappingEntry, axisValue: number): number {
  const t = axisNorm(axisValue);
  const v = m.base + mappingSlope(m) * t;
  return Math.min(m.max ?? Infinity, Math.max(m.min ?? -Infinity, v));
}

/** Evaluate a mapping entry (linear clamp). */
export function evalMapping(m: MappingEntry, axisValue: number): number {
  return clampMapped(m, axisValue);
}

function clamp01(v: number): number {
  return Math.max(0, Math.min(1, v));
}

function lerp(a: number, b: number, t: number): number {
  return a + (b - a) * clamp01(t);
}

function stickyMapping(): MappingEntry {
  return raidCatalog.profiles.mapping.stickyGain as MappingEntry;
}

function followMapping(): MappingEntry {
  return raidCatalog.profiles.mapping.adjutantFollowMul as MappingEntry;
}

/**
 * Bond willingness to follow adjutant (0..1).
 * Sourced from adjutantFollowMul / mapping max — bond's primary product meaning.
 */
export function adjutantCompliance01(bond: number): number {
  const m = followMapping();
  const follow = evalMapping(m, bond);
  const max = m.max ?? FOLLOW_MUL_MAX_DEFAULT;
  if (!(max > 0)) return 0;
  return clamp01(follow / max);
}

/**
 * Instinct×Bond composition (multiply) for adjutant-suggested motion pull once
 * a reaction is open. Instinct readiness is a separate timer (reactionDelaySec);
 * this returns path-compliance strength only.
 * - high bond × open reaction → pull toward adjutant seed
 * - low bond → personal geometry (compliance≈0)
 */
export function adjutantReactPull01(bond: number): number {
  return adjutantCompliance01(bond);
}

/**
 * personal01=1 at bond0 (high personal sticky), 0 at bond100 (open retarget).
 * Anchored on stickyGain evaluated at bond extremes so one authored curve drives family.
 * Preview label: personalSticky — not "bond strength".
 */
export function stickyPersonal01(stickyGain: number): number {
  const m = stickyMapping();
  const collective = evalMapping(m, 100);
  const personal = evalMapping(m, 0);
  if (personal <= collective) return 0;
  return clamp01((stickyGain - collective) / (personal - collective));
}

export function deriveMinSwitchGain(stickyGain: number): number {
  return lerp(
    DERIVED_MIN_SWITCH.collective,
    DERIVED_MIN_SWITCH.personal,
    stickyPersonal01(stickyGain),
  );
}

export function deriveStickyLeashPx(stickyGain: number): number {
  return lerp(
    DERIVED_LEASH_PX.collective,
    DERIVED_LEASH_PX.personal,
    stickyPersonal01(stickyGain),
  );
}

export function deriveCommitSec(stickyGain: number): number {
  return lerp(
    DERIVED_COMMIT_SEC.collective,
    DERIVED_COMMIT_SEC.personal,
    stickyPersonal01(stickyGain),
  );
}

export function deriveAdjutantPriorityBonus(followMul: number): number {
  const max = followMapping().max ?? FOLLOW_MUL_MAX_DEFAULT;
  if (!(max > 0)) return 0;
  return (Math.max(0, followMul) / max) * PRIORITY_BONUS_AT_FULL_FOLLOW;
}

/**
 * effective axis = classBase + authored byDefId offsets + userTuning(±tuningCap),
 * clamped 0..100. Offsets are table-driven (not hash quirks) so same-class heroes
 * keep role identity but read as distinct roster picks.
 * Sim Lab may set `aiSave.absolute` to force a full 0..100 value.
 */
export function resolveDisposition(
  defId: string,
  classId: RaidClassId,
): RaidDisposition {
  const p = raidCatalog.profiles;
  const abs = raidAiAbsoluteFor(defId);
  const base = p.classBase[classId] as RaidDisposition;
  const offsets =
    (
      p as {
        byDefId?: Record<string, { offsets?: Partial<RaidDisposition> }>;
      }
    ).byDefId?.[defId]?.offsets ?? {};
  const tuning = raidAiTuningFor(defId);
  const cap = p.tuningCap ?? 25;
  const out = {} as RaidDisposition;
  for (const axis of p.axes as RaidAxisId[]) {
    const forced = abs?.[axis];
    if (forced != null && Number.isFinite(forced)) {
      out[axis] = Math.max(0, Math.min(100, forced));
      continue;
    }
    const t = Math.max(-cap, Math.min(cap, tuning[axis] ?? 0));
    const o = offsets[axis] ?? 0;
    out[axis] = Math.max(0, Math.min(100, (base[axis] ?? 50) + o + t));
  }
  // P0 clarity: roster 통찰 = stand + pad (Sim Lab expand style).
  return symmetrizeInsightAxes(out);
}

/** Authored floor (no session tuning/absolute) — Sim Lab slider defaults. */
export function authoredStatsFloor(
  defId: string,
  classId: RaidClassId,
): RaidDisposition & { instinct: number } {
  const p = raidCatalog.profiles;
  const base = p.classBase[classId] as RaidDisposition;
  const offsets =
    (
      p as {
        byDefId?: Record<string, { offsets?: Partial<RaidDisposition> }>;
      }
    ).byDefId?.[defId]?.offsets ?? {};
  const out = {} as RaidDisposition;
  for (const axis of p.axes as RaidAxisId[]) {
    const o = offsets[axis] ?? 0;
    out[axis] = Math.max(0, Math.min(100, (base[axis] ?? 50) + o));
  }
  const ip = p as {
    instinct?: {
      classBase?: Record<string, number>;
      byDefId?: Record<string, number>;
    };
  };
  const instinct = Math.max(
    0,
    Math.min(
      100,
      (ip.instinct?.classBase?.[classId] ?? 50) +
        (ip.instinct?.byDefId?.[defId] ?? 0),
    ),
  );
  return { ...symmetrizeInsightAxes(out), instinct };
}

export function dispositionOf(u: RaidUnit): RaidDisposition {
  return resolveDisposition(u.defId, u.classId);
}

export function instinctOf(u: RaidUnit): number {
  return resolveInstinct(u.defId, u.classId);
}

/**
 * grit → surviveHpThr via linear mapping (base 0.5, per −0.01).
 * Endpoints: grit0→1.0 (chip flee), grit50→0.5, grit100→0 (never flee).
 */
export function surviveHpThrFor(grit: number): number {
  const m = raidCatalog.profiles.mapping.surviveHpThr as MappingEntry;
  return evalMapping(m, grit);
}

/**
 * Judgment Selling 통찰 fold — one number drives stand/flee/pad.
 * Authored measure≠selfProtect may still exist for quirks; runtime knobs
 * use the average so roster insight predicts behavior (clarity P0).
 */
export function insightAxisOf(d: RaidDisposition): number {
  const measure = d.measure ?? 50;
  const selfProtect = d.selfProtect ?? 50;
  return Math.max(0, Math.min(100, 0.5 * measure + 0.5 * selfProtect));
}

/** mapping value — temperament → AI bridge (authored keys only). */
export function dispositionKnob(
  d: RaidDisposition,
  key: keyof typeof raidCatalog.profiles.mapping,
): number {
  const m = raidCatalog.profiles.mapping[key] as MappingEntry;
  if (m.source === "instinct" || !m.axis) {
    if (!m.axis) return evalMapping({ ...m, slope: mappingSlope(m) }, 50);
    return evalMapping(m, 50);
  }
  // Clarity P0: measure/selfProtect-sourced knobs read insight fold.
  const axisId = m.axis as RaidAxisId;
  const axis =
    axisId === "measure" || axisId === "selfProtect"
      ? insightAxisOf(d)
      : (d[axisId] ?? 50);
  return evalMapping(m, axis);
}

/** Instinct-sourced knobs (reactionDelaySec, dodgeDistMul). */
export function instinctKnob(
  instinct: number,
  key: "reactionDelaySec" | "dodgeDistMul",
): number {
  const m = raidCatalog.profiles.mapping[key] as MappingEntry;
  return evalMapping(m, instinct);
}

/** Mid sticky → mid commit (fallback when bond unavailable). */
export function commitSecFixed(): number {
  return deriveCommitSec(evalMapping(stickyMapping(), 50));
}

/** Bond-driven behavior commit duration (유대0=긴 개인 커밋, 유대100=짧은 집단 재평가). */
export function commitSecForBond(bond: number): number {
  return deriveCommitSec(evalMapping(stickyMapping(), bond));
}

/**
 * Extra px past enemy AA for space latch / standoff (자기보호).
 * Floor contract remains AA-clear at pad 0 — this only widens the margin.
 */
export function aaClearPadPxFor(selfProtect: number): number {
  return evalMapping(
    raidCatalog.profiles.mapping.aaClearPadPx as MappingEntry,
    selfProtect,
  );
}

export function aaClearPadPxOf(u: RaidUnit): number {
  return aaClearPadPxFor(insightAxisOf(dispositionOf(u)));
}

/** @deprecated Use commitSecForBond — temperament focus absorbed into bond. */
export function commitSecForFocus(bondOrFocus: number): number {
  return commitSecForBond(bondOrFocus);
}

/**
 * Engage sticky knobs from bond (bipolar): low bond = personal sticky/leash;
 * high bond = open retarget toward collective priority.
 * Polar 통찰↑ hesitate is applied in raid-engage (insightRetargetHesitateMul).
 */
export function bondEngageSticky(u: RaidUnit): {
  stickyBonus: number;
  minSwitchGain: number;
  stickyLeashPx: number;
} {
  const stickyBonus = dispositionKnob(dispositionOf(u), "stickyGain");
  return {
    stickyBonus,
    minSwitchGain: deriveMinSwitchGain(stickyBonus),
    stickyLeashPx: deriveStickyLeashPx(stickyBonus),
  };
}

/** Soft collective focus score — derived from adjutantFollowMul. */
export function adjutantPriorityBonusOf(u: RaidUnit): number {
  return deriveAdjutantPriorityBonus(
    dispositionKnob(dispositionOf(u), "adjutantFollowMul"),
  );
}

/** @deprecated Use bondEngageSticky. */
export function focusEngageSticky(u: RaidUnit): {
  stickyBonus: number;
  minSwitchGain: number;
  stickyLeashPx: number;
} {
  return bondEngageSticky(u);
}

export type AiDerivedKnob = { key: string; value: number };

export type AiPrimaryId = RaidAxisId | "instinct";

/** Secondary knobs grouped by primary axis — Sim Lab preview surface. */
export type AiDerivedByPrimary = Record<AiPrimaryId, AiDerivedKnob[]>;

const EMPTY_DERIVED = (): AiDerivedByPrimary => ({
  grit: [],
  measure: [],
  selfProtect: [],
  bond: [],
  instinct: [],
});

/**
 * Map primary 0..100 axes → secondary AI knobs via dispositionKnob/instinctKnob.
 * Fixed mapping (no axis / not instinct) is omitted — not tied to a slider row.
 * Bond authored only (stickyGain / follow / healSplit); derived knobs are runtime.
 */
export function previewAiDerivedFromPrimaries(
  primaries: RaidDisposition & { instinct: number },
): AiDerivedByPrimary {
  const out = EMPTY_DERIVED();
  const d: RaidDisposition = {
    grit: primaries.grit,
    measure: primaries.measure,
    selfProtect: primaries.selfProtect,
    bond: primaries.bond,
  };
  const mapping = raidCatalog.profiles.mapping as Record<string, MappingEntry>;
  for (const key of Object.keys(mapping)) {
    const m = mapping[key]!;
    if (m.source === "instinct") {
      out.instinct.push({
        key,
        value: instinctKnob(
          primaries.instinct,
          key as "reactionDelaySec" | "dodgeDistMul",
        ),
      });
      continue;
    }
    if (!m.axis) continue;
    const axis = m.axis as RaidAxisId;
    if (!(axis in out)) continue;
    // stickyGain is bipolar personal stubbornness (↑ at bond↓) — label clearly
    // so Sim Lab does not read it as "bond strength".
    const displayKey = key === "stickyGain" ? "personalSticky" : key;
    out[axis].push({
      key: displayKey,
      value: dispositionKnob(
        d,
        key as keyof typeof raidCatalog.profiles.mapping,
      ),
    });
  }
  // Composition readout: bond willingness to follow adjutant (0..1).
  out.bond.push({
    key: "adjutantCompliance",
    value: adjutantCompliance01(primaries.bond),
  });
  return out;
}
